import { useState } from "react";
import {
  commonOutputProfiles,
  type Repository,
  type OutputProfile,
} from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Select, Button, Modal } from "./components";
type Group = ReturnType<typeof commonOutputProfiles>[number];
export function OutputProfileMerge({
  repository,
  tenantId,
  onMerged,
  notify,
}: {
  repository: Repository;
  tenantId: string;
  onMerged: () => void;
  notify: (message: string) => void;
}) {
  const transport = useWorkspaceTransport();
  const [groups, setGroups] = useState<Group[]>(),
    [selected, setSelected] = useState<Record<string, string>>({}),
    [busy, setBusy] = useState(false),
    [confirm, setConfirm] = useState(false),
    [comparing, setComparing] = useState<number>(),
    [choices, setChoices] = useState<Record<string, OutputProfile>>({});
  const invoke = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Profile merge failed. Reload and try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  if (!transport.demo && !transport.can("tenant.resources.manage")) return null;
  return (
    <section className="output-merge">
      <h2>Merge common output profiles</h2>
      <p>
        Find similar shared profiles with the same format, color space and
        output role. Compare their settings, then keep one profile for future
        use and archive the others. Higher DPI, image quality and minimum DPI
        are recommended first; vector output is preferred over rasterization.
        Existing document assignments, published versions and their pinned
        settings stay intact.
      </p>
      <Button
        disabled={busy}
        onClick={() =>
          void invoke(async () => {
            const rows = transport.demo
              ? commonOutputProfiles(
                  await repository.resources(tenantId, { scope: "shared" }),
                  await repository.tenantSettings(tenantId),
                )
              : (
                  await transport.api<{ groups: Group[] }>(
                    "/output-profiles/merge",
                    {
                      method: "POST",
                      body: JSON.stringify({ action: "preview" }),
                    },
                  )
                ).groups;
            setGroups(rows);
            setSelected({});
            setChoices({});
            setComparing(undefined);
            setConfirm(false);
          })
        }
      >
        {busy ? "Working…" : "Find common profiles"}
      </Button>
      {groups && groups.length === 0 && (
        <p role="status">No matching active shared profiles found.</p>
      )}
      {groups?.map((group, index) => (
        <fieldset key={index} disabled={busy}>
          <legend>
            <label>
              <input
                type="checkbox"
                checked={!!selected[String(index)]}
                onChange={(event) =>
                  setSelected((old) => {
                    const next = { ...old };
                    if (event.target.checked)
                      next[String(index)] = group.recommendedId;
                    else delete next[String(index)];
                    return next;
                  })
                }
              />{" "}
              Merge {group.profiles.length} matching profiles
            </label>
          </legend>
          <Button onClick={() => setComparing(index)}>
            Compare group {index + 1}
          </Button>
          <p>
            {group.profiles[0].value.format.toUpperCase()} ·{" "}
            {group.profiles[0].value.role || "Unspecified role"} ·{" "}
            {group.profiles.length} profiles
          </p>
          <label>
            Keep profile{" "}
            <Select
              aria-label={`Keep profile for group ${index + 1}`}
              value={selected[String(index)] || group.recommendedId}
              disabled={
                !selected[String(index)] ||
                group.profiles.some((row) => row.isDefault)
              }
              onChange={(event) =>
                setSelected((old) => ({
                  ...old,
                  [String(index)]: event.target.value,
                }))
              }
            >
              {group.profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                  {profile.isDefault ? " (workspace default)" : ""} · v
                  {profile.version}
                </option>
              ))}
            </Select>
          </label>
          <ul>
            {group.profiles.map((profile) => (
              <li key={profile.id}>
                {profile.name} · v{profile.version}
              </li>
            ))}
          </ul>
        </fieldset>
      ))}
      {comparing !== undefined &&
        groups?.[comparing] &&
        (() => {
          const group = groups[comparing];
          const merged = choices[String(comparing)] || group.recommended;
          const keys = [
            ...new Set(group.profiles.flatMap((p) => Object.keys(p.value))),
          ].filter((key) => !["id", "name", "outputPreset"].includes(key));
          const text = (value: unknown) =>
            value === undefined
              ? "Not set"
              : typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : String(value);
          return (
            <Modal
              title={`Compare output profiles · Group ${comparing + 1}`}
              onClose={() => setComparing(undefined)}
            >
              <p>
                Differences are highlighted. Choose the settings for the merged
                profile. Higher quality values are selected first; other
                settings start from the recommended profile. Existing documents
                keep their pinned settings.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Setting</th>
                      {group.profiles.map((p) => (
                        <th key={p.id}>
                          {p.name} · v{p.version}
                        </th>
                      ))}
                      <th>Merged settings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((key) => {
                      const k = key as keyof OutputProfile;
                      const values = group.profiles.map((p) => p.value[k]);
                      const different =
                        new Set(values.map((v) => JSON.stringify(v))).size > 1;
                      const options = [merged[k], ...values].filter(
                        (v, i, all) =>
                          all.findIndex(
                            (other) =>
                              JSON.stringify(other) === JSON.stringify(v),
                          ) === i,
                      );
                      return (
                        <tr
                          key={key}
                          style={
                            different
                              ? {
                                  background:
                                    "var(--surface-raised, rgba(128,128,128,.12))",
                                }
                              : undefined
                          }
                        >
                          <th>
                            {key.replace(/([A-Z])/g, " $1")}
                            {different ? " (differs)" : ""}
                          </th>
                          {values.map((v, i) => (
                            <td key={i}>
                              <pre
                                style={{
                                  whiteSpace: "pre-wrap",
                                  maxWidth: 260,
                                }}
                              >
                                {text(v)}
                              </pre>
                            </td>
                          ))}
                          <td>
                            {different ? (
                              <Select
                                aria-label={`Merged ${key}`}
                                value={options.findIndex(
                                  (v) =>
                                    JSON.stringify(v) ===
                                    JSON.stringify(merged[k]),
                                )}
                                onChange={(event) =>
                                  setChoices((old) => ({
                                    ...old,
                                    [String(comparing)]: {
                                      ...merged,
                                      [k]: options[Number(event.target.value)],
                                    },
                                  }))
                                }
                              >
                                {options.map((v, i) => (
                                  <option key={i} value={i}>
                                    {text(v)}
                                  </option>
                                ))}
                              </Select>
                            ) : (
                              text(merged[k])
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Button onClick={() => setComparing(undefined)}>
                Close comparison
              </Button>
              <Button
                kind="primary"
                onClick={() => {
                  setChoices((old) => ({
                    ...old,
                    [String(comparing)]: merged,
                  }));
                  setSelected((old) => ({
                    ...old,
                    [String(comparing)]:
                      old[String(comparing)] || group.recommendedId,
                  }));
                  setComparing(undefined);
                }}
              >
                Use these settings
              </Button>
            </Modal>
          );
        })()}
      {Object.keys(selected).length > 0 && (
        <Button kind="primary" disabled={busy} onClick={() => setConfirm(true)}>
          Review merge
        </Button>
      )}
      {confirm && (
        <Modal
          title="Merge matching output profiles?"
          onClose={() => {
            if (!busy) setConfirm(false);
          }}
        >
          <p>
            Keep the selected profiles and archive{" "}
            {Object.keys(selected).reduce(
              (sum, key) =>
                sum + (groups?.[Number(key)].profiles.length || 1) - 1,
              0,
            )}{" "}
            duplicates. The original profile versions are retained. Existing
            document settings remain unchanged. The survivor receives a new
            version when merged settings differ.
          </p>
          {Object.entries(selected).map(([index, id]) => (
            <p key={index}>
              Keep:{" "}
              <strong>
                {
                  groups?.[Number(index)].profiles.find((row) => row.id === id)
                    ?.name
                }
              </strong>
            </p>
          ))}
          <Button disabled={busy} onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button
            kind="primary"
            disabled={busy}
            onClick={() =>
              void invoke(async () => {
                const input = {
                  groups: Object.entries(selected).map(([index, id]) => {
                    const rows = groups![Number(index)].profiles;
                    const ref = (row: Group["profiles"][number]) => ({
                      id: row.id,
                      version: row.version,
                    });
                    return {
                      keep: ref(rows.find((row) => row.id === id)!),
                      duplicates: rows.filter((row) => row.id !== id).map(ref),
                      merged:
                        choices[index] || groups![Number(index)].recommended,
                    };
                  }),
                };
                if (transport.demo) {
                  if (!repository.mergeOutputProfiles)
                    throw new Error("Profile merging is unavailable.");
                  await repository.mergeOutputProfiles(tenantId, input);
                } else
                  await transport.api("/output-profiles/merge", {
                    method: "POST",
                    body: JSON.stringify({ action: "apply", ...input }),
                  });
                setConfirm(false);
                setGroups(undefined);
                setSelected({});
                onMerged();
                notify(
                  "Matching profiles merged. Existing document outputs were preserved.",
                );
              })
            }
          >
            {busy ? "Merging…" : "Merge profiles"}
          </Button>
        </Modal>
      )}
    </section>
  );
}
